import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import {
  useModalContextSubmissionSuccessful,
  useRefreshedRightSidebar,
  useRefreshModalCollectionCleared,
  useRefreshModalItemDeleted,
} from 'Hooks/useRefreshEvent';
import usePaginatedCollectionState from 'Hooks/usePaginatedCollectionState';

const ItemListContext = React.createContext(undefined);

const ItemListProvider = ({
  children,
}) => {
  const { pageContentResource } = usePageContentContext();
  const [pageNumber, setPageNumber] = React.useState(1);

  const {
    loading,
    loadingError,
    collection,
    refreshCollection,
    setCollection,
    hasNextPage,
    hasPrevPage,
    getNextPage: _getNextPage,
    getPrevPage: _getPrevPage,
  } = usePaginatedCollectionState({
    link: pageContentResource?.relationship(fr.relationships.items.name)?.link('related'),
    suppressError: true, // Handled by data container
  });

  const getPrevPage = React.useCallback(async () => {
    if (hasPrevPage) {
      await _getPrevPage({
        onSuccess: () => {
          setPageNumber((prevState) => prevState - 1);
        },
      });
    }
  }, [hasPrevPage, _getPrevPage, setPageNumber]);

  const getNextPage = React.useCallback(async () => {
    if (hasNextPage) {
      await _getNextPage({
        onSuccess: () => {
          setPageNumber((prevState) => prevState + 1);
        },
      });
    }
  }, [hasNextPage, _getNextPage, setPageNumber]);


  const memoizedValue = React.useMemo(() => {
    return {
      loading,
      loadingError,
      itemsCollection: collection,
      refreshItemsCollection: refreshCollection,
      setItemsCollection: setCollection,
      hasNextPage,
      hasPrevPage,
      pageNumber,
      getNextPage,
      getPrevPage,
    };
  }, [
    loading,
    loadingError,
    collection,
    refreshCollection,
    setCollection,
    hasNextPage,
    hasPrevPage,
    pageNumber,
    getNextPage,
    getPrevPage,
  ]);

  useRefreshedRightSidebar(() => {
    refreshCollection();
  }, [refreshCollection]);

  useModalContextSubmissionSuccessful(() => {
    refreshCollection();
  }, [refreshCollection]);

  useRefreshModalItemDeleted(() => {
    refreshCollection();
  }, [refreshCollection]);

  useRefreshModalCollectionCleared(() => {
    refreshCollection();
  }, [refreshCollection]);

  return (
    <ItemListContext.Provider value={memoizedValue}>
      {children}
    </ItemListContext.Provider>
  );
};

// Use ItemList State Context
const useItemListContext = () => {
  const context = React.useContext(ItemListContext);
  if (context === undefined) {
    throw new Error('useItemListContext must be used within a ItemListProvider');
  }
  return context;
};

ItemListProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  ItemListProvider,
  useItemListContext,
};
